import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";

/**
 * Compact affiliate-program CTA strip. Appears between FAQ and Footer so even
 * visitors who skip the nav see the Earn-5% offer.
 */
export default function AffiliateStrip() {
  const { t } = useI18n();
  const c = t.affiliate_strip;

  return (
    <section
      id="earn"
      className="relative bg-[var(--ink)] text-[var(--cream)] overflow-hidden"
      data-testid="affiliate-strip"
    >
      {/* subtle grain + marigold spotlight */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.5] pointer-events-none"
        style={{
          background:
            "radial-gradient(1200px 500px at 85% 50%, rgba(230,92,0,0.18), transparent 60%)",
        }}
      />
      <div className="container-x relative">
        <div className="py-16 md:py-24 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
          <div>
            <p
              className="font-mono-caption tracking-[0.3em] uppercase text-xs text-[var(--marigold)] mb-5"
              data-testid="affiliate-strip-eyebrow"
            >
              {c.eyebrow}
            </p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-5">
              {c.headline_a}{" "}
              <span className="font-editorial-italic text-[var(--marigold)]">
                {c.headline_italic}
              </span>
              {c.headline_b && (
                <>
                  <br />
                  {c.headline_b}
                </>
              )}
            </h2>
            <p className="text-base md:text-lg text-[var(--cream)]/70 max-w-xl mb-8 leading-relaxed">
              {c.body}
            </p>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/affiliate"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[var(--marigold)] text-white font-medium hover:bg-[#d14d00] transition-colors text-sm md:text-base"
                data-testid="affiliate-strip-primary-cta"
              >
                {c.cta_primary}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12h14M13 5l7 7-7 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </Link>
              <Link
                to="/affiliate/terms"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-[var(--cream)]/20 text-[var(--cream)] font-medium hover:bg-[var(--cream)]/5 transition-colors text-sm md:text-base"
                data-testid="affiliate-strip-terms-cta"
              >
                {c.cta_secondary}
              </Link>
            </div>
          </div>

          {/* Numeric highlight card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative justify-self-stretch lg:justify-self-end lg:max-w-[380px] w-full"
          >
            <div className="rounded-3xl bg-gradient-to-br from-[var(--marigold)]/15 to-transparent border border-[var(--cream)]/10 p-6 md:p-7 space-y-5">
              <Stat
                label={c.stat1_label}
                value={c.stat1_value}
                caption={c.stat1_caption}
              />
              <div className="h-px bg-[var(--cream)]/10" />
              <Stat
                label={c.stat2_label}
                value={c.stat2_value}
                caption={c.stat2_caption}
              />
              <div className="h-px bg-[var(--cream)]/10" />
              <Stat
                label={c.stat3_label}
                value={c.stat3_value}
                caption={c.stat3_caption}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, caption }) {
  return (
    <div>
      <div className="font-mono-caption tracking-[0.2em] uppercase text-[11px] text-[var(--cream)]/60 mb-1">
        {label}
      </div>
      <div className="font-display text-3xl md:text-4xl leading-none text-[var(--cream)]">
        {value}
      </div>
      <div className="text-xs md:text-sm text-[var(--cream)]/60 mt-1.5">
        {caption}
      </div>
    </div>
  );
}
